import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTag, listTags, TAG_COLORS } from '@littlebooker/dal';
import type { Tag, TagColor } from '@littlebooker/dal';
import { cn } from '@/lib/utils';
import { TagBadge, tagColorClasses } from './TagBadge';

type TagPickerProps = {
    /** Currently selected tag ids. */
    value: string[];
    onChange: (tagIds: string[]) => void;
};

/**
 * Select tags from the account's catalog, with inline creation.
 *
 * Creation lives here rather than only in a settings page because the moment a booker
 * needs a new label is while they are editing a show. A tag created here gets a colour
 * picked up front, since recolouring later means hunting it down in settings.
 */
export function TagPicker({ value, onChange }: TagPickerProps) {
    const queryClient = useQueryClient();
    const [draftLabel, setDraftLabel] = useState('');
    const [draftColor, setDraftColor] = useState<TagColor>('blue');

    const { data: tags = [], isLoading } = useQuery({
        queryKey: ['tags'],
        queryFn: () => listTags(),
        staleTime: 5 * 60_000,
    });

    const selected = useMemo(
        () => tags.filter((t) => value.includes(t.id)),
        [tags, value],
    );
    const unselected = useMemo(
        () => tags.filter((t) => !value.includes(t.id)),
        [tags, value],
    );

    const trimmed = draftLabel.trim();
    const alreadyExists = tags.some(
        (t) => t.label.toLowerCase() === trimmed.toLowerCase(),
    );

    const createMutation = useMutation({
        mutationFn: () => createTag({ label: trimmed, color: draftColor }),
        onSuccess: (created: Tag) => {
            queryClient.setQueryData<Tag[]>(['tags'], (prev) =>
                prev ? [...prev, created] : [created],
            );
            onChange([...value, created.id]);
            setDraftLabel('');
        },
    });

    const toggle = (tagId: string) => {
        onChange(
            value.includes(tagId)
                ? value.filter((id) => id !== tagId)
                : [...value, tagId],
        );
    };

    return (
        <div className="space-y-3">
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selected.map((tag) => (
                        <TagBadge
                            key={tag.id}
                            tag={tag}
                            className="px-3 py-1 text-sm"
                            onRemove={() => toggle(tag.id)}
                        />
                    ))}
                </div>
            )}

            {unselected.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {unselected.map((tag) => (
                        <button
                            key={tag.id}
                            type="button"
                            onClick={() => toggle(tag.id)}
                            className={cn(
                                'rounded-full border border-dashed border-input px-2 py-0.5 text-xs',
                                'text-muted-foreground hover:border-solid hover:bg-accent',
                            )}
                            title={tag.description || undefined}
                        >
                            + {tag.label}
                        </button>
                    ))}
                </div>
            )}

            {!isLoading && tags.length === 0 && (
                <p className="text-xs text-muted-foreground">
                    No tags yet. Create the first one below — it becomes available on every show.
                </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="text"
                    value={draftLabel}
                    onChange={(e) => setDraftLabel(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (trimmed && !alreadyExists) createMutation.mutate();
                        }
                    }}
                    placeholder="New tag label..."
                    className="flex-1 min-w-[12rem] rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <div className="flex items-center gap-1">
                    {TAG_COLORS.map((color) => (
                        <button
                            key={color}
                            type="button"
                            onClick={() => setDraftColor(color)}
                            aria-label={color}
                            title={color}
                            className={cn(
                                'h-6 w-6 rounded-full border-2',
                                tagColorClasses(color),
                                draftColor === color ? 'border-foreground' : 'border-transparent',
                            )}
                        />
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => createMutation.mutate()}
                    disabled={!trimmed || alreadyExists || createMutation.isPending}
                    className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
                >
                    {createMutation.isPending ? 'Adding...' : 'Add tag'}
                </button>
            </div>

            {alreadyExists && trimmed && (
                <p className="text-xs text-muted-foreground">
                    "{trimmed}" already exists — pick it from the list above.
                </p>
            )}
            {createMutation.isError && (
                <p className="text-xs text-red-badge-foreground">
                    Could not create the tag: {(createMutation.error as Error).message}
                </p>
            )}
        </div>
    );
}
